/**
 * Offline write capture for the global axios client.
 *
 * When the user is offline AND there's an active OfflineCheckSession,
 * outbound writes to the four endpoints below produce an
 * `error.request` (no `error.response`) — Axios's signal that no
 * response was ever received. Without this module the existing apiClient
 * error handler would just log to console and the data would be lost
 * (it stays in the optimistic Redux update but never reaches IndexedDB).
 *
 * This module inspects the failing request's URL + body, identifies
 * which session-manager method should record the write, persists it to
 * IndexedDB, and returns a synthetic 200-shaped response so the
 * redux-axios-middleware dispatches `*.SUCCESS` (instead of `*.ERROR`)
 * and the user-visible toast doesn't fire.
 *
 * Recognised endpoints (matches Phase 6's replay sequence in §3.8):
 *   - PATCH /avocados/{tempId}/      → sessionManager.updateFruitField
 *   - PATCH /checks/{tempId}/        → sessionManager.updateCheckField
 *   - POST  /defects/register-bulk   → sessionManager.updateFruitDefects
 *   - POST  /defects/register        → sessionManager.updateCheckDefect
 *     (check-level / intake defect frequencies, sent with fruit_id: null)
 *
 * Anything else is left alone — the existing error handler runs as
 * before and the request fails normally.
 */

import { getOfflineFeatureEnabled } from "./featureFlag";
import {
    getActiveSessionId,
    updateCheckDefect,
    updateCheckField,
    updateFruitDefects,
    updateFruitField,
} from "./sessionManager";
import type { OfflineDefect } from "./types";

interface SyntheticResponse {
    data: unknown;
    status: number;
    statusText: string;
    headers: Record<string, string>;
    config: unknown;
}

/**
 * Extract the request body axios stashed on the config. axios serializes
 * JSON to a string in `config.data`; in some retry paths it may already
 * be parsed. Be defensive.
 */
function readBody(rawData: unknown): Record<string, unknown> | null {
    if (!rawData) return null;
    if (typeof rawData === "object") return rawData as Record<string, unknown>;
    if (typeof rawData === "string") {
        try {
            const parsed = JSON.parse(rawData);
            return typeof parsed === "object" && parsed !== null ? parsed : null;
        } catch {
            return null;
        }
    }
    return null;
}

function flattenDefects(body: Record<string, unknown>): OfflineDefect[] {
    const defectIds = Array.isArray(body.defect_ids) ? (body.defect_ids as string[]) : [];
    const positions = Array.isArray(body.positions) ? (body.positions as string[]) : [];
    const severities = Array.isArray(body.severities) ? (body.severities as string[]) : [];
    const grouped: Record<string, OfflineDefect> = {};
    defectIds.forEach((id, idx) => {
        if (!grouped[id]) {
            grouped[id] = { defect_id: id, frequency: 0, positions: [], severities: [] };
        }
        grouped[id].frequency += 1;
        if (positions[idx] !== undefined) grouped[id].positions!.push(positions[idx]);
        if (severities[idx] !== undefined) grouped[id].severities!.push(severities[idx]);
    });
    return Object.values(grouped);
}

function syntheticResponse(config: unknown, data: unknown = {}): SyntheticResponse {
    return {
        data,
        status: 200,
        statusText: "OK (offline capture)",
        headers: {},
        config,
    };
}

/**
 * Try to capture an outbound write request that failed with `error.request`
 * (no response received) into the active offline session. Returns a
 * synthetic 200 response on success, or null if the request shouldn't be
 * captured (no active session, flag off, unrecognised URL).
 */
export async function tryCaptureOfflineRequest(
    error: { config?: { method?: string; url?: string; data?: unknown } },
): Promise<SyntheticResponse | null> {
    if (!getOfflineFeatureEnabled()) return null;
    const sessionId = getActiveSessionId();
    if (!sessionId) return null;

    const config = error?.config;
    if (!config?.method || !config.url) return null;

    const method = String(config.method).toUpperCase();
    // Strip the query string for the suffix-style match below.
    const path = config.url.split("?")[0];
    const body = readBody(config.data);

    // PATCH /avocados/{tempId}/ — single-field fruit update.
    // The path can arrive as either a full URL ("https://.../api/avocados/123/")
    // or relative ("avocados/123/") depending on whether the caller used
    // `callAvosApi` (prepends base_url) or a Redux thunk via the
    // redux-axios middleware (leaves the URL relative). Anchor with
    // `(?:^|/)` so both forms match.
    const avocadoMatch = path.match(/(?:^|\/)avocados\/(\d+)\/?$/);
    if (method === "PATCH" && avocadoMatch && body) {
        const tempId = parseInt(avocadoMatch[1], 10);
        const field = body.field;
        const value = body.value;
        if (typeof field !== "string") return null;
        try {
            await updateFruitField(sessionId, tempId, field, value);
            return syntheticResponse(config);
        } catch {
            return null;
        }
    }

    // PATCH /checks/{tempId}/ — single-field check-level update.
    const checkMatch = path.match(/(?:^|\/)checks\/(\d+)\/?$/);
    if (method === "PATCH" && checkMatch && body) {
        const field = body.field;
        const value = body.value;
        if (typeof field !== "string") return null;
        try {
            await updateCheckField(sessionId, field, value);
            return syntheticResponse(config);
        } catch {
            return null;
        }
    }

    // POST /defects/register-bulk — replace the defect list on a fruit.
    if (method === "POST" && /(?:^|\/)defects\/register-bulk\/?$/.test(path) && body) {
        const fruitId = body.fruit_id;
        if (typeof fruitId !== "number") return null;
        const defects = flattenDefects(body);
        try {
            await updateFruitDefects(sessionId, fruitId, defects);
            return syntheticResponse(config);
        } catch {
            return null;
        }
    }

    // POST /defects/register — single defect registration. The visual/intake
    // "defect frequency" form registers check-level defects here with
    // `fruit_id: null` (fruit-level defects go through register-bulk above).
    // Body: { check_id, fruit_id, defect_id, frequency }. The `\/?$` anchor
    // keeps this from also matching `defects/register-bulk`.
    if (method === "POST" && /(?:^|\/)defects\/register\/?$/.test(path) && body) {
        const defectId = body.defect_id;
        // Only check-level (fruit_id null/absent) registrations belong to the
        // offline check session; the replay model has no per-fruit single
        // register, so leave anything fruit-scoped for the normal error path.
        if (typeof defectId !== "string") return null;
        if (body.fruit_id !== null && body.fruit_id !== undefined) return null;
        const frequency = typeof body.frequency === "number"
            ? body.frequency
            : Number(body.frequency) || 0;
        try {
            await updateCheckDefect(sessionId, defectId, frequency);
            return syntheticResponse(config);
        } catch {
            return null;
        }
    }

    return null;
}
